import flagSmith from "@flagsmith/flagsmith";
import { isInternalSupportUser, createAuth } from "src/services/auth";

const environmentID = process.env.REACT_APP_BULLET_TRAIN_ENVIRONMENT_ID;
const defaultFlags = {};

// Flagsmith must not block app bootstrap. SDK bugs (e.g. non-array /flags/
// payloads → TypeError: forEach is not a function) can leave init hanging
// without calling onChange/onError; always settle so isFeatureFlagsLoaded flips.
const FLAGSMITH_INIT_TIMEOUT_MS = 8000;

const loadFeatureFlags = (user) => {
  const { username, email } = createAuth(user);
  const isSupportUser = isInternalSupportUser(user);

  const cacheFlags = process.env.NODE_ENV !== "development";

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId;
    const settle = (flags = defaultFlags) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(flags || defaultFlags);
    };

    timeoutId = setTimeout(() => {
      settle(defaultFlags);
    }, FLAGSMITH_INIT_TIMEOUT_MS);

    const cleanedUsername =
      username && username.includes("-")
        ? `${username.replace(/-/g, "")}fixed`
        : username;

    // Avoid calling /flags/ (no-identity path) with a falsy identity — that
    // path is where the SDK has crashed on unexpected response shapes.
    const identity =
      (isSupportUser && email) || cleanedUsername || email || "anonymous";

    try {
      flagSmith
        .init({
          environmentID,
          // Support users with an email use it as their Flagsmith identity so
          // toggles can be targeted; otherwise fall back to the cleaned username.
          identity,
          traits: {
            username: cleanedUsername || identity,
            // email trait sent for everyone so email-based segments (e.g. the
            // backend-consistent debug-tabs gate) match any targeted user.
            ...(email && { email }),
          },
          defaultFlags,
          api: "https://edge.api.flagsmith.com/api/v1/",
          cacheFlags,
          cacheOptions: {
            ttl: 1000 * 3600 * 2, // 2 hours in ms
            skipAPI: true,
          },
          onError: () => settle(defaultFlags),
          onChange: () => {
            try {
              settle(flagSmith.getAllFlags());
            } catch {
              settle(defaultFlags);
            }
          },
        })
        .catch(() => settle(defaultFlags));
    } catch {
      settle(defaultFlags);
    }
  });
};

export default loadFeatureFlags;
export { FLAGSMITH_INIT_TIMEOUT_MS };
